import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { classAPI, activityAPI, walletAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Coins, ArrowLeft, Users, Target, Store, Wallet, Clock, CheckCircle2, Copy, TrendingUp, ShoppingBag, Award, LayoutDashboard, Gamepad2, Trophy, BarChart3, History, Share2, MessageSquare, Mail, Send, MoreHorizontal, UserPlus, ShieldCheck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatCoins, formatDate, formatDateTime, getStatusLabel, getLedgerTypeLabel } from '../lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Import sub-components
import StudentsTab from '../components/class/StudentsTab';
import ActivitiesTab from '../components/class/ActivitiesTab';
import MarketplaceTab from '../components/class/MarketplaceTab';
import ApprovalsTab from '../components/class/ApprovalsTab';
import StudentDashboard from '../components/class/StudentDashboard';
import GamificationTab from '../components/class/GamificationTab';
import LeaderboardTab from '../components/class/LeaderboardTab';
import AnalyticsTab from '../components/class/AnalyticsTab';
import CompletionsHistoryTab from '../components/class/CompletionsHistoryTab';
import MessagesTab from '../components/class/MessagesTab';
import ActivityFeed from '../components/class/ActivityFeed';
import AssistantsManagerDialog from '../components/class/AssistantsManagerDialog';

export default function ClassView() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [classData, setClassData] = useState(null);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  // isManager = current user has professor-level access for this class (owner OR assistant OR admin)
  const isManager = !!classData?.is_manager;
  const isOwner = !!classData?.is_owner;
  const isAssistant = !!classData?.is_assistant;
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedCode, setCopiedCode] = useState(false);
  const [openGroupChatId, setOpenGroupChatId] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [assistantsDialogOpen, setAssistantsDialogOpen] = useState(false);
  const tabScrollRef = useRef(null);

  // Listen for bottom nav tab events (mobile)
  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail);
    window.addEventListener('bottom-nav-tab', handler);
    return () => window.removeEventListener('bottom-nav-tab', handler);
  }, []);

  // Handle navigation state from notifications
  useEffect(() => {
    const navState = location.state;
    if (!navState) return;

    if (navState.tab === 'chat') {
      setActiveTab(isManager ? 'students' : 'dashboard');
      if (navState.openGroupChat) {
        setOpenGroupChatId(navState.openGroupChat);
      }
    } else if (navState.tab === 'dashboard') {
      setActiveTab(isManager ? 'overview' : 'dashboard');
    } else if (navState.tab === 'activities') {
      setActiveTab('activities');
    }
    window.history.replaceState({}, '');
  }, [location.state, isManager]);

  // Auto-scroll active tab into view on mobile
  useEffect(() => {
    if (!tabScrollRef.current) return;
    const active = tabScrollRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  useEffect(() => {
    loadClassData();
  }, [classId]);

  const loadClassData = async () => {
    try {
      const [classRes, activitiesRes] = await Promise.all([
        classAPI.get(classId),
        activityAPI.list(classId),
      ]);
      setClassData(classRes.data);
      setActivities(activitiesRes.data);

      const managerView = !!classRes.data?.is_manager;
      if (managerView) {
        const statsRes = await classAPI.getStats(classId);
        setStats(statsRes.data);
      } else {
        const walletRes = await walletAPI.get(classId);
        setWallet(walletRes.data);
        setActiveTab((prev) => (prev === 'overview' ? 'dashboard' : prev));
      }
    } catch (error) {
      toast.error('Error al cargar la clase');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(classData?.join_code);
    setCopiedCode(true);
    toast.success('Código copiado');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleInviteByEmail = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await classAPI.inviteByEmail(classId, inviteEmail.trim());
      toast.success(`Invitación enviada a ${inviteEmail}`);
      setInviteEmail('');
      setInviteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar invitación');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Tab configuration
  const getTabs = () => {
    if (isManager) {
      return [
        { value: 'overview', icon: TrendingUp, label: 'Resumen', primary: true },
        { value: 'students', icon: Users, label: 'Estudiantes', primary: true },
        { value: 'activities', icon: Target, label: 'Actividades', primary: true },
        { value: 'messages', icon: MessageSquare, label: 'Mensajes', primary: true },
        { value: 'marketplace', icon: Store, label: 'Tienda', primary: true },
        { value: 'approvals', icon: Clock, label: 'Aprobaciones', badge: stats?.pending_listings, primary: true },
        { value: 'gamification', icon: Gamepad2, label: 'Gamificacion' },
        { value: 'leaderboard', icon: Trophy, label: 'Ranking' },
        { value: 'analytics', icon: BarChart3, label: 'Analytics' },
        { value: 'history', icon: History, label: 'Historial' },
      ];
    }
    return [
      { value: 'dashboard', icon: LayoutDashboard, label: 'Progreso', primary: true },
      { value: 'activities', icon: Target, label: 'Actividades', primary: true },
      { value: 'marketplace', icon: Store, label: 'Tienda', primary: true },
      { value: 'wallet', icon: Wallet, label: 'Billetera', primary: true },
      { value: 'leaderboard', icon: Trophy, label: 'Ranking', primary: true },
    ];
  };

  const tabs = getTabs();
  const primaryTabs = tabs.filter(t => t.primary);
  const overflowTabs = tabs.filter(t => !t.primary);

  return (
    <div data-testid="class-view">
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Class Header - Compact on mobile */}
        <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 h-8 w-8 flex-shrink-0 hidden sm:flex"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-2xl font-heading font-bold text-slate-900 truncate" data-testid="class-name">{classData?.name}</h1>
              <p className="text-[11px] sm:text-sm text-slate-500 truncate">{classData?.semester}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {isManager && (
              <>
                {/* Desktop: show all buttons */}
                <button
                  onClick={copyCode}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-mono font-medium text-slate-700 transition-colors"
                  data-testid="copy-join-code"
                >
                  {copiedCode ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  {classData?.join_code}
                </button>
                <Button variant="outline" size="sm" className="h-8 hidden sm:flex"
                  onClick={() => {
                    const link = `${window.location.origin}/join/${classData?.join_code}`;
                    if (navigator.share) {
                      navigator.share({ title: `Únete a ${classData?.name}`, url: link }).catch(() => {});
                    } else {
                      navigator.clipboard.writeText(link);
                      toast.success('Enlace copiado');
                    }
                  }}
                  data-testid="share-invite-link"
                >
                  <Share2 className="w-4 h-4 mr-1" />Link
                </Button>
                <Button variant="default" size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 hidden sm:flex"
                  onClick={() => setInviteDialogOpen(true)}
                  data-testid="invite-by-email-btn"
                >
                  <Mail className="w-4 h-4 mr-1" />Invitar
                </Button>

                {/* Mobile: actions in dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8 sm:hidden" data-testid="class-actions-mobile">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={copyCode}>
                      <Copy className="w-4 h-4 mr-2" />Código: {classData?.join_code}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      const link = `${window.location.origin}/join/${classData?.join_code}`;
                      if (navigator.share) {
                        navigator.share({ title: `Únete a ${classData?.name}`, url: link }).catch(() => {});
                      } else {
                        navigator.clipboard.writeText(link);
                        toast.success('Enlace copiado');
                      }
                    }}>
                      <Share2 className="w-4 h-4 mr-2" />Compartir Link
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setInviteDialogOpen(true)}>
                      <Mail className="w-4 h-4 mr-2" />Invitar por Email
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            
            {!isManager && wallet && (
              <div className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-amber-50 border border-amber-100 rounded-xl">
                <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                <span className="font-heading font-bold text-sm sm:text-base text-amber-700" data-testid="wallet-balance">
                  {formatCoins(wallet.balance)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile-friendly horizontal scroll tabs */}
        <div className="relative mb-4 sm:mb-6">
          <div
            ref={tabScrollRef}
            className="flex overflow-x-auto gap-1.5 sm:gap-2 pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 no-scrollbar snap-x snap-mandatory scroll-smooth items-center"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {primaryTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  data-active={isActive}
                  onClick={() => setActiveTab(tab.value)}
                  className={`snap-center whitespace-nowrap flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all flex-shrink-0 relative active:scale-95 ${
                    isActive
                      ? 'bg-slate-900 text-white shadow-sm shadow-slate-900/20'
                      : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300 hover:text-slate-700 hover:shadow-sm'
                  }`}
                  data-testid={`tab-${tab.value}`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {tab.label}
                  {tab.badge > 0 && (
                    <span className={`ml-1 w-5 h-5 text-[10px] font-bold rounded-full flex items-center justify-center ${
                      isActive ? 'bg-amber-400 text-slate-900' : 'bg-amber-500 text-white'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
            {overflowTabs.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`snap-center whitespace-nowrap flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all flex-shrink-0 active:scale-95 ${
                      overflowTabs.some(t => t.value === activeTab)
                        ? 'bg-slate-900 text-white shadow-sm shadow-slate-900/20'
                        : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300 hover:text-slate-700 hover:shadow-sm'
                    }`}
                    data-testid="tab-more-menu"
                  >
                    <MoreHorizontal className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {overflowTabs.some(t => t.value === activeTab)
                      ? overflowTabs.find(t => t.value === activeTab)?.label
                      : 'Mas'
                    }
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  {overflowTabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <DropdownMenuItem
                        key={tab.value}
                        onClick={() => setActiveTab(tab.value)}
                        className={activeTab === tab.value ? 'bg-slate-100 font-semibold' : ''}
                        data-testid={`tab-${tab.value}`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {tab.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {/* Fade indicator on right edge */}
          <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none sm:hidden" />
        </div>

        {/* Tab Content */}
        <div>
          {/* Student Dashboard Tab */}
          {!isManager && activeTab === 'dashboard' && (
            <StudentDashboard classId={classId} user={user} classData={classData} openGroupChatId={openGroupChatId} onGroupChatOpened={() => setOpenGroupChatId(null)} />
          )}

          {/* Professor Overview Tab */}
          {isManager && activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card className="border-slate-200/80 card-hover">
                  <CardContent className="p-4 sm:pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">Estudiantes</p>
                        <p className="text-2xl sm:text-3xl font-heading font-bold text-slate-900" data-testid="stat-students">
                          {stats?.total_students || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200/80 card-hover">
                  <CardContent className="p-4 sm:pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">Coins Emitidos</p>
                        <p className="text-2xl sm:text-3xl font-heading font-bold text-amber-600" data-testid="stat-coins">
                          {formatCoins(stats?.total_coins_issued || 0)}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <Coins className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200/80 card-hover">
                  <CardContent className="p-4 sm:pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">XP Otorgado</p>
                        <p className="text-2xl sm:text-3xl font-heading font-bold text-purple-600" data-testid="stat-xp">
                          {stats?.total_xp_issued || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200/80 cursor-pointer card-hover hover:border-amber-200" onClick={() => setActiveTab('approvals')}>
                  <CardContent className="p-4 sm:pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">Pendientes</p>
                        <p className="text-2xl sm:text-3xl font-heading font-bold text-slate-900" data-testid="stat-pending">
                          {stats?.pending_listings || 0}
                        </p>
                        <p className="text-[10px] text-amber-600 font-medium mt-0.5">Ver aprobaciones</p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ActivityFeed classId={classId} />

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Información de la Clase</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-slate-500">Código de Acceso</p>
                      <p className="text-2xl font-mono font-bold text-slate-900">{classData?.join_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Profesor</p>
                      <p className="font-medium text-slate-900">{classData?.professor_name}</p>
                    </div>
                    {classData?.description && (
                      <div>
                        <p className="text-sm text-slate-500">Descripción</p>
                        <p className="text-slate-700">{classData.description}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Fecha de Creación</p>
                      <p className="text-slate-700">{formatDate(classData?.created_at)}</p>
                    </div>

                    {/* Assistants section */}
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-500 flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4 text-emerald-500" />
                          Asistentes ({(classData?.assistants || []).length})
                        </p>
                        {isOwner && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setAssistantsDialogOpen(true)}
                            data-testid="manage-assistants-btn"
                          >
                            <UserPlus className="w-3.5 h-3.5 mr-1" />
                            Gestionar
                          </Button>
                        )}
                      </div>
                      {(classData?.assistants || []).length === 0 ? (
                        <p className="text-xs text-slate-400 italic">
                          {isOwner ? 'Agrega profesores o estudiantes como asistentes' : 'Sin asistentes designados'}
                        </p>
                      ) : (
                        <div className="space-y-1.5" data-testid="assistants-list">
                          {(classData.assistants || []).map((a) => (
                            <div key={a.user_id} className="flex items-center gap-2 text-sm">
                              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {a.photo_url ? (
                                  <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-500">
                                    {(a.name || '?').slice(0, 2).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-slate-800 font-medium truncate">{a.name}</p>
                                <p className="text-[11px] text-slate-500 truncate">{a.email}</p>
                              </div>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                a.role === 'professor'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {a.role === 'professor' ? 'Profesor' : 'Estudiante'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {isAssistant && !isOwner && (
                        <p className="mt-2 text-[11px] text-emerald-600 font-medium">
                          Estás actuando como Asistente de esta clase
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Students Tab (Professor) */}
          {isManager && activeTab === 'students' && (
            <StudentsTab classId={classId} activities={activities} onRefresh={loadClassData} openGroupChatId={openGroupChatId} onGroupChatOpened={() => setOpenGroupChatId(null)} isOwner={isOwner} className={classData?.name} />
          )}

          {/* Messages Tab */}
          {isManager && activeTab === 'messages' && (
            <MessagesTab classId={classId} currentUser={user} />
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <ActivitiesTab classId={classId} isProfessor={isManager} onRefresh={loadClassData} />
          )}

          {/* Marketplace Tab */}
          {activeTab === 'marketplace' && (
            <MarketplaceTab classId={classId} isProfessor={isManager} wallet={wallet} onRefresh={loadClassData} />
          )}

          {/* Wallet Tab (Student) */}
          {!isManager && activeTab === 'wallet' && (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-xl sm:text-2xl">Mi Billetera</CardTitle>
                    <CardDescription>Historial de transacciones</CardDescription>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-sm text-slate-500">Balance Actual</p>
                    <div className="flex items-center gap-2 text-2xl sm:text-3xl font-heading font-bold text-amber-600">
                      <Coins className="w-6 h-6 sm:w-8 sm:h-8" />
                      {formatCoins(wallet?.balance || 0)}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {wallet?.ledger?.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-xl"
                      data-testid={`ledger-entry-${entry.id}`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          entry.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                        }`}>
                          {entry.amount > 0 ? (
                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                          ) : (
                            <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm sm:text-base text-slate-900 truncate">{entry.note || getLedgerTypeLabel(entry.type)}</p>
                          <p className="text-xs sm:text-sm text-slate-500">{formatDateTime(entry.created_at)}</p>
                        </div>
                      </div>
                      <div className={`font-heading font-bold text-base sm:text-lg flex-shrink-0 ml-2 ${entry.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {entry.amount > 0 ? '+' : ''}{formatCoins(entry.amount)}
                      </div>
                    </div>
                  ))}
                  {(!wallet?.ledger || wallet.ledger.length === 0) && (
                    <div className="text-center py-12">
                      <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay transacciones aún</p>
                      <p className="text-sm text-slate-400">Completa actividades para ganar coins</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Gamification Tab (Professor) */}
          {isManager && activeTab === 'gamification' && (
            <GamificationTab classId={classId} onRefresh={loadClassData} />
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <LeaderboardTab classId={classId} />
          )}

          {/* Analytics Tab (Professor) */}
          {isManager && activeTab === 'analytics' && (
            <AnalyticsTab classId={classId} />
          )}

          {/* Completions History Tab (Professor) */}
          {isManager && activeTab === 'history' && (
            <CompletionsHistoryTab classId={classId} />
          )}

          {/* Approvals Tab (Professor) */}
          {isManager && activeTab === 'approvals' && (
            <ApprovalsTab classId={classId} onRefresh={loadClassData} />
          )}
        </div>
      </main>

      {/* Invite by Email Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Invitar Estudiante por Correo
            </DialogTitle>
            <DialogDescription>
              Se enviará un correo de invitación con el enlace para unirse a <strong>{classData?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Correo del estudiante</Label>
              <Input
                type="email"
                placeholder="estudiante@correo.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInviteByEmail()}
                data-testid="invite-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInviteDialogOpen(false); setInviteEmail(''); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleInviteByEmail}
              disabled={inviting || !inviteEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-invite-btn"
            >
              {inviting ? 'Enviando...' : (
                <>
                  <Send className="w-4 h-4 mr-1.5" />
                  Enviar Invitación
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assistants Manager Dialog (owner only) */}
      {isOwner && (
        <AssistantsManagerDialog
          open={assistantsDialogOpen}
          onOpenChange={setAssistantsDialogOpen}
          classId={classId}
          className={classData?.name}
          onChange={loadClassData}
        />
      )}
    </div>
  );
}
